<template>
  <div class="xfpaint-page">
    <header class="xfpaint-topbar">
      <a class="brand" href="/xfpaint" aria-label="小范共享去水印工具官方页面">
        <span class="brand-mark">XF</span>
        <span class="brand-text">小范共享去水印工具</span>
      </a>

      <nav class="top-links" aria-label="产品导航">
        <a href="#overview">概览</a>
        <a href="#features">亮点</a>
        <a href="#workflow">流程</a>
        <a href="#faq">常见问题</a>
      </nav>

      <div class="top-actions">
        <a class="ghost-link" href="https://fanjunjie.cn" target="_self">返回主站</a>
        <a class="download-link" :href="downloadUrl" target="_blank" rel="noopener noreferrer">
          下载
        </a>
      </div>
    </header>

    <section id="overview" class="hero">
      <div class="hero-copy">
        <p class="hero-kicker">XFShare Local Retouch</p>
        <h1>
          本地离线去水印，
          <span>正式交付更省心。</span>
        </h1>
        <p class="hero-description">
          小范共享去水印工具是一款面向 Windows 用户的本地 CPU 离线图片修复工具，
          专注处理水印、字幕、角落 Logo、背景杂物与轻度瑕疵。安装后直接使用，
          不需要额外配置 Python、模型目录或命令行环境。
        </p>

        <div class="hero-pills">
          <span v-for="item in heroPills" :key="item">{{ item }}</span>
        </div>
      </div>

      <div class="hero-visual" aria-hidden="true">
        <div class="device-card">
          <div class="device-topbar">
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div class="device-stage">
            <div class="stage-sidebar">
              <div class="sidebar-chip is-active">修复</div>
              <div class="sidebar-chip">裁剪</div>
              <div class="sidebar-chip">导出</div>
            </div>

            <div class="stage-canvas">
              <div class="canvas-image">
                <div class="watermark watermark-one">WATERMARK</div>
                <div class="watermark watermark-two">字幕遮挡</div>
                <div class="mask mask-a"></div>
                <div class="mask mask-b"></div>
              </div>

              <div class="floating-toolbar">
                <span>笔刷</span>
                <span>撤销</span>
                <span>预览</span>
                <span>保存</span>
              </div>
            </div>
          </div>

          <div class="device-footer">
            <div class="metric-card">
              <small>运行方式</small>
              <strong>Local CPU</strong>
            </div>
            <div class="metric-card">
              <small>适用系统</small>
              <strong>Windows 10 / 11</strong>
            </div>
            <div class="metric-card">
              <small>导出格式</small>
              <strong>JPG / PNG / WebP</strong>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="story-strip">
      <article v-for="item in storyCards" :key="item.title" class="story-card">
        <p>{{ item.kicker }}</p>
        <h2>{{ item.title }}</h2>
        <span>{{ item.description }}</span>
      </article>
    </section>

    <section id="features" class="feature-stack">
      <article class="feature-panel">
        <div class="panel-copy">
          <p>快上手</p>
          <h2>安装后就能开修，不把用户丢给命令行。</h2>
          <span>
            软件把 IOPaint 的图像修复能力封装成独立安装包，用户直接从桌面快捷方式打开，
            不需要自己处理 Python、依赖环境、模型目录或本地服务地址。
          </span>
        </div>
        <div class="panel-visual panel-visual-soft">
          <div class="mini-window">
            <div class="mini-window-bar"></div>
            <div class="mini-window-body">
              <div class="dropzone">拖入图片或点击选择图片</div>
              <div class="mini-tag-row">
                <span>本地处理</span>
                <span>CPU 稳定版</span>
                <span>一键修复</span>
              </div>
            </div>
          </div>
        </div>
      </article>

      <article class="feature-panel reverse">
        <div class="panel-copy">
          <p>稳处理</p>
          <h2>把重点放在兼容性和稳定性，而不是只追求参数好看。</h2>
          <span>
            当前版本定位为 CPU 离线稳定版，普通 Windows 电脑也能运行。适合办公环境、
            客户电脑、无独显设备，重点解决“能不能稳定安装、能不能稳定打开、能不能稳定导出”。
          </span>
        </div>
        <div class="panel-visual panel-visual-strong">
          <div class="status-board">
            <div class="status-row">
              <strong>单实例运行</strong>
              <span>避免端口冲突和模型重复加载</span>
            </div>
            <div class="status-row">
              <strong>本地日志</strong>
              <span>启动异常可定位模型缺失或权限问题</span>
            </div>
            <div class="status-row">
              <strong>未保存提醒</strong>
              <span>降低误关闭造成的数据丢失</span>
            </div>
          </div>
        </div>
      </article>

      <article class="feature-panel">
        <div class="panel-copy">
          <p>官方感</p>
          <h2>不只是一个能用的工具，而是一套可以正式交付的产品体验。</h2>
          <span>
            页面和软件表达都强调“本地、稳定、离线、修复”，并配套裁剪、导出、格式转换、
            保存目录管理与快捷键说明，让用户从第一次打开就知道这款工具能帮他完成什么。
          </span>
        </div>
        <div class="panel-visual panel-visual-light">
          <div class="capsule-grid">
            <div class="capsule">Big-LaMa 图像修复</div>
            <div class="capsule">导出目录可控</div>
            <div class="capsule">格式真实转换</div>
            <div class="capsule">支持裁剪与撤销</div>
            <div class="capsule">本机离线处理</div>
            <div class="capsule">普通电脑可运行</div>
          </div>
        </div>
      </article>
    </section>

    <section id="workflow" class="workflow">
      <div class="section-heading">
        <p>使用流程</p>
        <h2>从导入图片到导出成品，过程尽量短，反馈尽量清楚。</h2>
      </div>

      <div class="workflow-grid">
        <article v-for="step in steps" :key="step.index" class="workflow-card">
          <span>{{ step.index }}</span>
          <h3>{{ step.title }}</h3>
          <p>{{ step.description }}</p>
        </article>
      </div>
    </section>

    <section class="specs">
      <div class="section-heading">
        <p>适配能力</p>
        <h2>常见图片任务和常用导出需求，一页交代清楚。</h2>
      </div>

      <div class="specs-layout">
        <div class="spec-column">
          <h3>适合处理</h3>
          <div class="spec-list">
            <article v-for="item in suitableCases" :key="item.title" class="spec-item">
              <strong>{{ item.title }}</strong>
              <span>{{ item.description }}</span>
            </article>
          </div>
        </div>

        <div class="spec-column">
          <h3>导出格式</h3>
          <div class="format-list">
            <article v-for="item in formats" :key="item.name" class="format-item">
              <strong>{{ item.name }}</strong>
              <span>{{ item.description }}</span>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section id="faq" class="faq">
      <div class="section-heading">
        <p>常见问题</p>
        <h2>把用户最关心的问题提前说清楚。</h2>
      </div>

      <div class="faq-list">
        <details v-for="item in faqs" :key="item.question" class="faq-item">
          <summary>{{ item.question }}</summary>
          <p>{{ item.answer }}</p>
        </details>
      </div>
    </section>
  </div>
</template>

<script setup>
import { watchEffect } from "vue";

const downloadUrl =
  "https://pan.baidu.com/s/1nAnLh_0yhM9YTiILO6QrqA?pwd=rxnd";

const heroPills = [
  "本地 CPU 离线去水印",
  "Big-LaMa 图像修复",
  "Windows 10 / 11 64 位",
  "裁剪导出与格式转换",
];

const storyCards = [
  {
    kicker: "本地处理",
    title: "图片默认留在本机",
    description: "适合对隐私和交付环境有要求的本地使用场景。",
  },
  {
    kicker: "CPU 稳定版",
    title: "普通电脑也能运行",
    description: "不依赖独立显卡，重点保证兼容性和可安装性。",
  },
  {
    kicker: "完整流程",
    title: "从修复到导出一气呵成",
    description: "支持裁剪、保存目录管理、格式转换和常用快捷键。",
  },
];

const steps = [
  {
    index: "01",
    title: "安装并启动",
    description: "运行安装程序，桌面生成快捷方式，双击即可打开本地独立窗口。",
  },
  {
    index: "02",
    title: "导入图片",
    description: "点击选择图片或直接拖拽导入，进入编辑工作区。",
  },
  {
    index: "03",
    title: "涂抹遮罩",
    description: "用笔刷标记水印、字幕、Logo 或轻度瑕疵区域。",
  },
  {
    index: "04",
    title: "本地 AI 修复",
    description: "Big-LaMa 模型结合周围纹理补全画面，用户可随时撤销重做。",
  },
  {
    index: "05",
    title: "裁剪并导出",
    description: "按需调整比例、选择格式、保存到指定目录，避免覆盖历史结果。",
  },
];

const suitableCases = [
  {
    title: "角落 Logo / 小水印",
    description: "适配度高，适合小范围遮挡或背景相对连续的区域。",
  },
  {
    title: "字幕 / 轻度杂物",
    description: "适合短视频封面、海报、截图等场景的局部清理。",
  },
  {
    title: "复杂主体遮挡",
    description: "人物五官、手部、商品关键结构建议缩小笔刷分多次修复。",
  },
];

const formats = [
  {
    name: "JPG / JPEG",
    description: "照片、电商图、通用分享，兼容性强、体积较小。",
  },
  {
    name: "PNG",
    description: "边缘清晰或需要透明背景的场景，适合精细图像。",
  },
  {
    name: "WebP",
    description: "适合网页和移动端，压缩效率高，文件体积更小。",
  },
  {
    name: "BMP / TIFF / GIF",
    description: "兼容老软件、专业归档或静态 GIF 输出等特定需求。",
  },
];

const faqs = [
  {
    question: "为什么软件第一次启动会比较慢？",
    answer:
      "首次启动需要加载本地模型，CPU 和磁盘性能较弱的电脑会更慢一些，后续通常会更稳定。",
  },
  {
    question: "为什么有些图去水印后不够自然？",
    answer:
      "如果遮罩过大，或遮挡了人物五官、手部、商品关键结构，模型只能推测内容。建议缩小笔刷、分多次处理。",
  },
  {
    question: "保存后为什么找不到图片？",
    answer:
      "请在设置中确认当前保存文件夹，并用“打开文件夹”功能直接定位导出结果。",
  },
  {
    question: "这款工具会把图片上传到云端吗？",
    answer:
      "正常使用时图片处理在本机完成，默认不会上传到云端或第三方服务器。",
  },
];

watchEffect(() => {
  document.title = "小范共享去水印工具 | 官方页面";
});
</script>

<style scoped>
.xfpaint-page {
  min-height: 100vh;
  padding: 24px 24px 80px;
  color: #202124;
  background:
    radial-gradient(circle at top left, rgba(166, 196, 255, 0.2), transparent 26%),
    radial-gradient(circle at right 10%, rgba(223, 246, 210, 0.5), transparent 18%),
    linear-gradient(180deg, #f7f9fc 0%, #ffffff 36%, #f8fbff 100%);
}

.xfpaint-topbar,
.hero,
.story-strip,
.feature-stack,
.workflow,
.specs,
.faq,
.download-panel {
  max-width: 1280px;
  margin: 0 auto;
}

.xfpaint-topbar {
  position: sticky;
  top: 18px;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
  padding: 14px 18px;
  border: 1px solid rgba(32, 33, 36, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
  box-shadow: 0 12px 34px rgba(60, 64, 67, 0.08);
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1a73e8, #34a853);
  color: #fff;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.brand-text {
  font-size: 0.98rem;
  font-weight: 600;
}

.top-links,
.top-actions {
  display: flex;
  align-items: center;
  gap: 18px;
}

.top-links a,
.ghost-link {
  color: #5f6368;
  text-decoration: none;
  font-size: 0.95rem;
}

.download-link,
.primary-button,
.secondary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid transparent;
  font: inherit;
  cursor: pointer;
  text-decoration: none;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease;
}

.download-link,
.primary-button {
  background: #1a73e8;
  color: #fff;
  box-shadow: 0 12px 24px rgba(26, 115, 232, 0.22);
}

.secondary-button {
  background: #fff;
  color: #202124;
  border-color: rgba(60, 64, 67, 0.15);
}

.secondary-button.light {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.16);
}

.download-link:hover,
.primary-button:hover,
.secondary-button:hover {
  transform: translateY(-1px);
}

.hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(420px, 0.92fr);
  gap: 34px;
  align-items: center;
  padding: 44px 0 12px;
}

.hero-copy {
  max-width: 620px;
}

.hero-kicker,
.section-heading p,
.story-card p,
.panel-copy p,
.download-copy p {
  margin: 0;
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #1a73e8;
}

.hero h1,
.section-heading h2,
.story-card h2,
.panel-copy h2,
.download-copy h2 {
  margin: 0;
}

.hero h1 {
  margin-top: 16px;
  font-size: clamp(2.8rem, 5vw, 4.6rem);
  line-height: 1.02;
  letter-spacing: -0.05em;
}

.hero h1 span {
  display: block;
  margin-top: 8px;
  color: #5f6368;
}

.hero-description,
.story-card span,
.panel-copy span,
.workflow-card p,
.spec-item span,
.format-item span,
.faq-item p,
.download-copy span {
  color: #5f6368;
  line-height: 1.8;
}

.hero-description {
  margin-top: 18px;
  max-width: 560px;
  font-size: 1rem;
}

.hero-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}

.hero-pills span,
.sidebar-chip,
.capsule {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid rgba(60, 64, 67, 0.12);
  color: #3c4043;
  font-size: 0.92rem;
}

.hero-visual {
  position: relative;
}

.device-card,
.story-card,
.feature-panel,
.workflow-card,
.spec-column,
.faq-item,
.download-panel {
  border: 1px solid rgba(60, 64, 67, 0.1);
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 24px 60px rgba(60, 64, 67, 0.08);
}

.device-card {
  padding: 18px;
  border-radius: 36px;
}

.device-topbar {
  display: flex;
  gap: 8px;
  padding-bottom: 14px;
}

.device-topbar span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #dadce0;
}

.device-stage {
  display: grid;
  grid-template-columns: 118px 1fr;
  gap: 14px;
}

.stage-sidebar,
.stage-canvas,
.mini-window,
.status-board {
  border-radius: 26px;
}

.stage-sidebar {
  display: grid;
  align-content: start;
  gap: 10px;
  padding: 14px;
  background: linear-gradient(180deg, #eef4ff, #f6f9ff);
}

.sidebar-chip.is-active {
  background: #1a73e8;
  color: #fff;
  border-color: transparent;
}

.stage-canvas {
  position: relative;
  padding: 18px;
  background: linear-gradient(180deg, #f7fafc, #edf3fb);
  min-height: 430px;
}

.canvas-image {
  position: relative;
  height: 100%;
  border-radius: 28px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(52, 168, 83, 0.26), rgba(26, 115, 232, 0.22)),
    linear-gradient(180deg, #e7eef7, #f9fbff);
}

.canvas-image::before,
.canvas-image::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.68);
  filter: blur(2px);
}

.canvas-image::before {
  width: 210px;
  height: 210px;
  top: 46px;
  left: 56px;
}

.canvas-image::after {
  width: 170px;
  height: 170px;
  right: 48px;
  bottom: 58px;
}

.watermark {
  position: absolute;
  z-index: 2;
  color: rgba(95, 99, 104, 0.46);
  font-weight: 700;
  letter-spacing: 0.08em;
}

.watermark-one {
  top: 72px;
  right: 54px;
  transform: rotate(-12deg);
}

.watermark-two {
  left: 60px;
  bottom: 86px;
}

.mask {
  position: absolute;
  z-index: 3;
  border-radius: 999px;
  border: 2px dashed rgba(26, 115, 232, 0.65);
  background: rgba(26, 115, 232, 0.16);
}

.mask-a {
  width: 148px;
  height: 54px;
  top: 82px;
  right: 44px;
}

.mask-b {
  width: 138px;
  height: 46px;
  left: 48px;
  bottom: 76px;
}

.floating-toolbar {
  position: absolute;
  left: 50%;
  bottom: 26px;
  z-index: 4;
  display: flex;
  gap: 10px;
  padding: 10px;
  border: 1px solid rgba(60, 64, 67, 0.1);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  transform: translateX(-50%);
  box-shadow: 0 10px 28px rgba(60, 64, 67, 0.12);
}

.floating-toolbar span {
  padding: 8px 14px;
  border-radius: 999px;
  background: #f1f3f4;
  font-size: 0.88rem;
}

.device-footer {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.metric-card {
  padding: 16px;
  border-radius: 22px;
  background: linear-gradient(180deg, #ffffff, #f7f9fc);
}

.metric-card small {
  display: block;
  color: #5f6368;
}

.metric-card strong {
  display: block;
  margin-top: 6px;
  font-size: 0.98rem;
}

.story-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  padding-top: 26px;
}

.story-card {
  padding: 24px;
  border-radius: 28px;
}

.story-card h2 {
  margin-top: 10px;
  font-size: 1.3rem;
  line-height: 1.2;
}

.story-card span {
  display: block;
  margin-top: 14px;
}

.feature-stack,
.workflow,
.specs,
.faq {
  padding-top: 72px;
}

.feature-stack {
  display: grid;
  gap: 22px;
}

.feature-panel {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(320px, 1.08fr);
  gap: 26px;
  align-items: center;
  padding: 26px;
  border-radius: 32px;
}

.feature-panel.reverse {
  grid-template-columns: minmax(320px, 1.08fr) minmax(0, 0.92fr);
}

.feature-panel.reverse .panel-copy {
  order: 2;
}

.feature-panel.reverse .panel-visual {
  order: 1;
}

.panel-copy h2 {
  margin-top: 12px;
  font-size: clamp(1.7rem, 2.7vw, 2.6rem);
  line-height: 1.1;
  letter-spacing: -0.04em;
}

.panel-copy span {
  display: block;
  margin-top: 14px;
  font-size: 0.98rem;
}

.panel-visual {
  min-height: 360px;
  border-radius: 32px;
  overflow: hidden;
}

.panel-visual-soft {
  padding: 20px;
  background: linear-gradient(135deg, #f5f9ff, #eef7f0);
}

.mini-window {
  height: 100%;
  padding: 20px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(60, 64, 67, 0.1);
}

.mini-window-bar {
  height: 14px;
  width: 120px;
  border-radius: 999px;
  background: #e8eaed;
}

.mini-window-body {
  display: grid;
  align-content: center;
  justify-items: center;
  height: calc(100% - 14px);
}

.dropzone {
  display: grid;
  place-items: center;
  width: min(100%, 390px);
  min-height: 190px;
  border: 2px dashed rgba(26, 115, 232, 0.3);
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(26, 115, 232, 0.06), rgba(52, 168, 83, 0.06));
  color: #3c4043;
  font-size: 1.06rem;
  text-align: center;
}

.mini-tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}

.mini-tag-row span {
  padding: 8px 14px;
  border-radius: 999px;
  background: #f1f3f4;
  color: #3c4043;
  font-size: 0.9rem;
}

.panel-visual-strong {
  display: grid;
  place-items: center;
  padding: 24px;
  background: linear-gradient(135deg, #16325c, #1a73e8);
}

.status-board {
  display: grid;
  gap: 14px;
  width: 100%;
  max-width: 460px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(16px);
}

.status-row {
  padding: 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
}

.status-row strong,
.status-row span,
.spec-item strong,
.format-item strong,
.faq-item summary {
  display: block;
}

.status-row span {
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.7;
}

.panel-visual-light {
  display: grid;
  place-items: center;
  padding: 24px;
  background: linear-gradient(180deg, #f8fbff, #f3f8ff);
}

.capsule-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  max-width: 460px;
  justify-content: center;
}

.capsule {
  min-height: 46px;
  padding: 0 18px;
  font-size: 0.95rem;
}

.section-heading {
  max-width: 760px;
}

.section-heading h2 {
  margin-top: 12px;
  font-size: clamp(1.8rem, 3vw, 2.8rem);
  line-height: 1.1;
  letter-spacing: -0.04em;
}

.workflow-grid,
.faq-list {
  display: grid;
  gap: 16px;
  margin-top: 28px;
}

.workflow-grid {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.workflow-card {
  padding: 22px;
  border-radius: 24px;
}

.workflow-card span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #e8f0fe;
  color: #1a73e8;
  font-weight: 700;
}

.workflow-card h3 {
  margin: 14px 0 0;
  font-size: 1rem;
}

.workflow-card p {
  margin: 12px 0 0;
}

.specs-layout {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  margin-top: 28px;
}

.spec-column {
  padding: 24px;
  border-radius: 28px;
}

.spec-column h3 {
  margin: 0;
  font-size: 1.08rem;
}

.spec-list,
.format-list {
  display: grid;
  gap: 14px;
  margin-top: 20px;
}

.spec-item,
.format-item {
  padding: 18px 0;
  border-top: 1px solid rgba(60, 64, 67, 0.1);
}

.spec-item:first-child,
.format-item:first-child {
  padding-top: 0;
  border-top: 0;
}

.spec-item span,
.format-item span {
  margin-top: 8px;
}

.faq-item {
  padding: 18px 22px;
  border-radius: 22px;
}

.faq-item summary {
  cursor: pointer;
  font-weight: 600;
  list-style: none;
}

.faq-item summary::-webkit-details-marker {
  display: none;
}

.faq-item p {
  margin: 12px 0 0;
}

@media (max-width: 1180px) {
  .hero,
  .feature-panel,
  .feature-panel.reverse,
  .specs-layout {
    grid-template-columns: 1fr;
  }

  .feature-panel.reverse .panel-copy,
  .feature-panel.reverse .panel-visual {
    order: initial;
  }

  .story-strip,
  .workflow-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .xfpaint-page {
    padding: 14px 14px 56px;
  }

  .xfpaint-topbar {
    position: static;
    flex-direction: column;
    align-items: stretch;
    border-radius: 28px;
  }

  .top-links,
  .top-actions,
  .hero-pills,
  .device-footer,
  .story-strip,
  .workflow-grid,
  .specs-layout {
    grid-template-columns: 1fr;
  }

  .top-links,
  .top-actions {
    flex-wrap: wrap;
  }

  .hero {
    grid-template-columns: 1fr;
    padding-top: 26px;
  }

  .device-stage {
    grid-template-columns: 1fr;
  }

  .stage-sidebar {
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
  }

  .story-strip {
    display: grid;
  }

  .feature-panel,
  .workflow-card,
  .spec-column,
  .faq-item,
  .story-card {
    padding: 22px;
  }

  .panel-visual {
    min-height: 280px;
  }
}
</style>
